export const SPORTSNEW_SELECTED_RECOVERY_LIMIT = 50;

const positiveSafeInteger = (value) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

const oddStateIdOf = (value) => positiveSafeInteger(
  value?.oddStateId
    ?? value?.odd_state_id
    ?? value?.odds_id
    ?? value,
);

export const normalizeSelectedRecoveryIds = (values = []) => [...new Set(
  (Array.isArray(values) ? values : [])
    .map(oddStateIdOf)
    .filter((value) => value != null),
)]
  .sort((left, right) => left - right)
  .slice(0, SPORTSNEW_SELECTED_RECOVERY_LIMIT);

export const selectedRecoveryRequestKey = (oddStateIds, displayScope = '') => (
  `${String(displayScope || '').trim().toLowerCase()}|${normalizeSelectedRecoveryIds(oddStateIds).join(',')}`
);

const recoveryPayload = (source = {}) => {
  if (Array.isArray(source?.odds)) return source;
  if (Array.isArray(source?.data?.odds)) return source.data;
  if (Array.isArray(source?.data?.data?.odds)) return source.data.data;
  return source?.data && typeof source.data === 'object' ? source.data : source;
};

const finiteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const timestampOrNull = (value) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const selectedPatchFreshness = (patch = {}, sequence = null, observedAt = Date.now()) => ({
  sourceReceivedAt: timestampOrNull(patch.sourceReceivedAt ?? patch.source_received_at),
  updatedAt: timestampOrNull(patch.updatedAt ?? patch.updated_at),
  sequence,
  observedAt,
});

export const shouldPreserveNewerSelectedWs = (restUpdate, wsAtRequest, latestWs) => {
  if (!latestWs) return false;

  const restSourceTime = restUpdate?.sourceReceivedAt;
  const wsSourceTime = latestWs.sourceReceivedAt;
  if (Number.isFinite(restSourceTime) && Number.isFinite(wsSourceTime)) {
    if (restSourceTime !== wsSourceTime) return wsSourceTime > restSourceTime;

    const restUpdatedAt = restUpdate?.updatedAt;
    const wsUpdatedAt = latestWs.updatedAt;
    if (Number.isFinite(restUpdatedAt) && Number.isFinite(wsUpdatedAt)) {
      return wsUpdatedAt >= restUpdatedAt;
    }
    return true;
  }

  const restUpdatedAt = restUpdate?.updatedAt;
  const wsUpdatedAt = latestWs.updatedAt;
  if (Number.isFinite(restUpdatedAt) && Number.isFinite(wsUpdatedAt)) {
    return wsUpdatedAt >= restUpdatedAt;
  }

  // Missing rows carry no REST timestamp. Only a same-id WS that arrived
  // after request start can prevent that row from becoming unavailable.
  return latestWs !== wsAtRequest;
};

const lockedState = (odd = {}, priceDecimal) => {
  const state = String(odd.state ?? odd.displayState ?? '').trim().toUpperCase();
  return priceDecimal == null
    || odd.bettable === false
    || odd.isBettable === false
    || odd.locked === true
    || odd.suspended === true
    || odd.is_suspended === true
    || ['LOCKED', 'SUSPENDED', 'UNAVAILABLE'].includes(state);
};

export const normalizeSelectedRecoveryResult = (source, requestedValues = []) => {
  const requestedIds = normalizeSelectedRecoveryIds(requestedValues);
  const requested = new Set(requestedIds);
  const payload = recoveryPayload(source);
  const odds = Array.isArray(payload?.odds) ? payload.odds : [];
  const found = new Set();
  const updates = [];

  odds.forEach((odd) => {
    const oddStateId = oddStateIdOf(odd);
    if (oddStateId == null || !requested.has(oddStateId) || found.has(oddStateId)) return;

    found.add(oddStateId);
    const priceDecimal = finiteNumberOrNull(
      odd.priceDecimal ?? odd.price_decimal ?? odd.odds ?? odd.price,
    );
    const locked = lockedState(odd, priceDecimal);
    updates.push({
      oddStateId,
      priceDecimal,
      ...selectedPatchFreshness(odd, null, null),
      patch: {
        bettable: !locked,
        locked,
        is_suspended: odd.suspended === true || odd.is_suspended === true,
      },
    });
  });

  const explicitMissing = normalizeSelectedRecoveryIds(
    payload?.missingOddStateIds ?? payload?.missing_odd_state_ids ?? [],
  );
  const missing = new Set([
    ...requestedIds.filter((oddStateId) => !found.has(oddStateId)),
    ...explicitMissing.filter((oddStateId) => requested.has(oddStateId)),
  ]);

  missing.forEach((oddStateId) => {
    updates.push({
      oddStateId,
      priceDecimal: null,
      sourceReceivedAt: null,
      updatedAt: null,
      patch: { unavailable: true, bettable: false },
    });
  });

  return {
    requestedIds,
    updates,
    missingOddStateIds: [...missing].sort((left, right) => left - right),
  };
};

export const recoverSelectedOddsCoalesced = ({
  inFlightRequests,
  submittedSelections,
  displayScope,
  recoverSelected,
  wsFreshnessById,
  isSelectionActive = () => true,
  applyUpdate = () => {},
}) => {
  const oddStateIds = normalizeSelectedRecoveryIds(submittedSelections);
  if (!(inFlightRequests instanceof Map) || typeof recoverSelected !== 'function' || oddStateIds.length === 0) {
    return Promise.resolve({ applied: false, reason: 'no_selected_odds', oddStateIds });
  }

  const requestKey = selectedRecoveryRequestKey(oddStateIds, displayScope);
  const existingRequest = inFlightRequests.get(requestKey);
  if (existingRequest) return existingRequest;

  const freshness = wsFreshnessById instanceof Map ? wsFreshnessById : new Map();
  const wsFreshnessAtRequest = new Map(oddStateIds.map((oddStateId) => [
    oddStateId,
    freshness.get(oddStateId),
  ]));

  const request = (async () => {
    try {
      const response = await recoverSelected({ oddStateIds, displayScope });
      if (response?.status !== true) {
        return { applied: false, reason: 'request_failed', oddStateIds, response };
      }

      const normalized = normalizeSelectedRecoveryResult(response, oddStateIds);
      const skippedOddStateIds = [];
      const skippedInactiveOddStateIds = [];
      normalized.updates.forEach((update) => {
        const { oddStateId } = update;
        if (!isSelectionActive(oddStateId)) {
          skippedInactiveOddStateIds.push(oddStateId);
          return;
        }
        if (shouldPreserveNewerSelectedWs(
          update,
          wsFreshnessAtRequest.get(oddStateId),
          freshness.get(oddStateId),
        )) {
          skippedOddStateIds.push(oddStateId);
          return;
        }
        applyUpdate(update);
      });

      return {
        applied: true,
        reason: 'selected_rest_refresh',
        oddStateIds,
        missingOddStateIds: normalized.missingOddStateIds,
        skippedOddStateIds,
        skippedInactiveOddStateIds,
      };
    } catch (error) {
      return { applied: false, reason: 'request_error', oddStateIds, error };
    }
  })();

  inFlightRequests.set(requestKey, request);
  request.finally(() => {
    if (inFlightRequests.get(requestKey) === request) inFlightRequests.delete(requestKey);
  });
  return request;
};
